// React Query mutation hooks for modifying SMS data

import { useApiClient } from '@commander/api-client';
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreateThreadData, SendMessageData, SMSThreadDetailsResponse } from '../../api/smsApi';
import { createSmsApi } from '../../api/smsApi';
import type { SMSMessage } from '../../types';
import { communicationKeys } from '../communicationKeys';

/**
 * Hook to create a new SMS thread and send first message with automatic cache invalidation
 */
export function useCreateSMSThread() {
  const apiClient = useApiClient();
  const smsApi = createSmsApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateThreadData) => smsApi.createThread(data),
    onSuccess: () => {
      // Invalidate and refetch SMS thread lists
      queryClient.invalidateQueries({ queryKey: communicationKeys.sms.threads() });
      queryClient.invalidateQueries({
        queryKey: communicationKeys.sms.threadDetails(),
      });
      // Invalidate unified inbox so new SMS threads appear immediately
      queryClient.invalidateQueries({ queryKey: communicationKeys.unified.lists() });
    },
  });
}

/**
 * Hook to send an SMS message in an existing thread with optimistic updates and cache invalidation
 */
export function useSendSMSMessage() {
  const apiClient = useApiClient();
  const smsApi = createSmsApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ threadId, data }: { threadId: string; data: SendMessageData }) =>
      smsApi.sendMessage(threadId, data),

    // Optimistically update the cache before the server responds
    onMutate: async ({ threadId, data }) => {
      // Cancel any outgoing refetches to prevent them from overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: communicationKeys.sms.threadDetails() });

      // Capture all query keys and their data that match the pattern for this thread
      const queryCache = queryClient.getQueryCache();
      const threadDetailQueries = queryCache.findAll({
        queryKey: communicationKeys.sms.threadDetails(),
      });

      // Filter for queries matching this specific threadId and snapshot their data
      const previousQueries = threadDetailQueries
        .map((query) => {
          const queryKey = query.queryKey as readonly unknown[];
          // Check if this query key includes our threadId
          // Query key structure: ['sms', 'threadDetail', threadId, params]
          if (queryKey[2] === threadId) {
            return {
              queryKey: query.queryKey,
              data: query.state.data as InfiniteData<SMSThreadDetailsResponse> | undefined,
            };
          }
          return null;
        })
        .filter(
          (
            entry
          ): entry is {
            queryKey: readonly unknown[];
            data: InfiniteData<SMSThreadDetailsResponse> | undefined;
          } => entry !== null
        );

      // Optimistically update the cache with the new message
      // Only update queries for the specific thread to avoid showing the message in all threads
      for (const { queryKey } of previousQueries) {
        queryClient.setQueryData<InfiniteData<SMSThreadDetailsResponse>>(queryKey, (old) => {
          if (!old) return old;

          // Create optimistic message
          const optimisticMessage: SMSMessage = {
            id: `temp-${Date.now()}`, // Temporary ID
            type: 'sms',
            direction: 'outgoing',
            contactId: old.pages[0]?.conversation?.contactId || '',
            contactName: old.pages[0]?.conversation?.contactName || '',
            phoneNumber: old.pages[0]?.conversation?.phoneNumber || '',
            subject: 'SMS',
            content: data.body,
            message: data.body,
            segments: Math.ceil(data.body.length / 160),
            channel: 'sms',
            conversationId: threadId,
            timestamp: new Date(),
            status: 'sent',
            mediaUrls: [],
            metadata: {},
            createdBy: '',
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          // Add the optimistic message to the most recent page (first page)
          return {
            ...old,
            pages: old.pages.map((page, index) => {
              if (index === 0 && page.conversation) {
                return {
                  ...page,
                  conversation: {
                    ...page.conversation,
                    messages: [...page.conversation.messages, optimisticMessage],
                    lastMessage: data.body,
                    lastMessageAt: new Date(),
                  },
                };
              }
              return page;
            }),
          };
        });
      }

      // Return context with all affected query snapshots for potential rollback
      return { previousQueries };
    },

    // On success, invalidate to get the real server data
    onSuccess: () => {
      // Invalidate thread lists to refetch with updated data
      queryClient.invalidateQueries({ queryKey: communicationKeys.sms.threads() });
      // Invalidate all thread detail queries (broader pattern to catch all pagination params)
      queryClient.invalidateQueries({
        queryKey: communicationKeys.sms.threadDetails(),
      });
    },

    // On error, roll back the optimistic update
    onError: (_error, _variables, context) => {
      if (context?.previousQueries) {
        // Restore all affected queries to their previous state
        for (const { queryKey, data } of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
  });
}

/**
 * Hook to delete an SMS message with automatic cache invalidation
 */
export function useDeleteSMSMessage() {
  const apiClient = useApiClient();
  const smsApi = createSmsApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ threadId, messageId }: { threadId: string; messageId: string }) =>
      smsApi.deleteMessage(threadId, messageId),
    onSuccess: (_, variables) => {
      // Invalidate thread lists to refetch without deleted message
      queryClient.invalidateQueries({ queryKey: communicationKeys.sms.threads() });
      // Also invalidate the specific thread detail
      queryClient.invalidateQueries({
        queryKey: communicationKeys.sms.threadDetail(variables.threadId),
      });
    },
  });
}

/**
 * Hook to mark SMS message as read/unread with automatic cache invalidation
 */
export function useMarkSMSMessageRead() {
  const apiClient = useApiClient();
  const smsApi = createSmsApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      threadId,
      messageId,
      isRead,
    }: {
      threadId: string;
      messageId: string;
      isRead: boolean;
    }) => smsApi.markMessageRead(threadId, messageId, isRead),
    onSuccess: (_, variables) => {
      // Invalidate thread lists to show updated read status
      queryClient.invalidateQueries({ queryKey: communicationKeys.sms.threads() });
      // Also invalidate the specific thread detail
      queryClient.invalidateQueries({
        queryKey: communicationKeys.sms.threadDetail(variables.threadId),
      });
      // Also invalidate unified inbox to reflect read status changes
      queryClient.invalidateQueries({ queryKey: communicationKeys.unified.lists() });
      // Invalidate notifications as they may be marked as read
      queryClient.invalidateQueries({ queryKey: communicationKeys.notifications.lists() });
    },
  });
}

/**
 * Hook to star/unstar an SMS message with automatic cache invalidation
 */
export function useMarkSMSMessageStar() {
  const apiClient = useApiClient();
  const smsApi = createSmsApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      threadId,
      messageId,
      isStarred,
    }: {
      threadId: string;
      messageId: string;
      isStarred: boolean;
    }) => smsApi.markMessageStar(threadId, messageId, isStarred),
    onSuccess: (_, variables) => {
      // Invalidate thread lists to show updated star status
      queryClient.invalidateQueries({ queryKey: communicationKeys.sms.threads() });
      // Also invalidate the specific thread detail
      queryClient.invalidateQueries({
        queryKey: communicationKeys.sms.threadDetail(variables.threadId),
      });
      // Also invalidate unified inbox to reflect star changes
      queryClient.invalidateQueries({ queryKey: communicationKeys.unified.lists() });
    },
  });
}

/**
 * Hook to mark all incoming unread messages in a thread as read with automatic cache invalidation
 */
export function useMarkSMSThreadRead() {
  const apiClient = useApiClient();
  const smsApi = createSmsApi(apiClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (threadId: string) => smsApi.markThreadRead(threadId),
    onSuccess: (_, threadId) => {
      // Invalidate thread lists to show updated unread counts
      queryClient.invalidateQueries({ queryKey: communicationKeys.sms.threads() });
      // Also invalidate the specific thread detail to show updated read status
      queryClient.invalidateQueries({
        queryKey: communicationKeys.sms.threadDetail(threadId),
      });
      // Also invalidate unified inbox to reflect read status changes
      queryClient.invalidateQueries({ queryKey: communicationKeys.unified.lists() });
      // Invalidate notifications as they may be marked as read
      queryClient.invalidateQueries({ queryKey: communicationKeys.notifications.lists() });
    },
  });
}
